import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Flex, InputRef } from "antd";
import { getClientInactiveAnalytics } from "hooks/queries/analytics";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { tnum } from "shared/components/TableText";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { ClientInactiveRow, InactivityThresholdDays } from "shared/types";
import PATHS from "shared/constants/paths";
import InlineDaysInput from "./InlineDaysInput";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { useAuth } from "context/AuthContextProvider";
import { useTableSort } from "hooks/useTableSort";
import { thresholdStamp } from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { inactiveSpec } from "pdf/reports/specs/archetype3";
import { useReportPageCount } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

const INACTIVITY_DAYS_MIN = 7;
const INACTIVITY_DAYS_MAX = 365;

const ClientInactivePage = () => {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { sortState, onSortChange } = useTableSort();
  const [threshold, setThreshold] = useState<InactivityThresholdDays>(60);
  const searchInput = useRef<InputRef>(null);
  const { data, isLoading } = getClientInactiveAnalytics(threshold);

  const columns: ReportColumn<ClientInactiveRow>[] = [
    reportColumn<ClientInactiveRow>("client", {
      dataIndex: "customer_name",
      search: getColumnSearchProps<ClientInactiveRow, "customer_name">({
        config: "customer_name",
        searchInput,
      }),
    }),
    reportColumn<ClientInactiveRow>("days_inactive", {
      dataIndex: "days_inactive",
      render: (value: number) => <span style={{ ...tnum, fontWeight: 600 }}>{value}</span>,
      sorter: (a, b) => a.days_inactive - b.days_inactive,
      defaultSortOrder: "descend",
    }),
    reportColumn<ClientInactiveRow>("last_product", {
      dataIndex: "last_package_name",
      // The long text of this four-column table.
      share: 30,
      min: 220,
    }),
    reportColumn<ClientInactiveRow>("expiry", { dataIndex: "expired_at" }),
  ];

  const pageCount = useReportPageCount(inactiveSpec, data, sortState);

  return (
    <>
      <ReportPageHeading
        title={translate("analyticsPage.client.report_inactive")}
        backTo={PATHS.ANALYTICS}
        extra={
          <ExportMenu
            reportSlug="klien-tidak-aktif"
            params={{ days: threshold }}
            pdf={
              data && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(data.table.length, MAX_PDF_ROWS),
                  total: data.table.length,
                },
                fileName: `spott-${"klien-tidak-aktif"}-${thresholdStamp(threshold)}.pdf`,
                render: async (maxRows) => {
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: "klien-tidak-aktif",
                    studio: authState.business?.name ?? "",
                    data: data,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        {/* The rule itself, front and center — the day count is the only
            control on the page, set right where it's explained rather than
            in a button group the sentence below just describes. */}
        <Card styles={{ body: { padding: 22 } }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: textPx(17), color: colors.ink }}>
            {translate("analyticsPage.client.inactive_rule_heading")}
          </div>
          <Flex align="center" gap={10} wrap="wrap" style={{ marginTop: 12 }}>
            <span style={{ fontSize: textPx(14), fontWeight: 500, color: colors.ink2 }}>
              {translate("analyticsPage.client.inactive_rule_prefix")}
            </span>
            <InlineDaysInput
              value={threshold}
              onChange={setThreshold}
              min={INACTIVITY_DAYS_MIN}
              max={INACTIVITY_DAYS_MAX}
            />
            <span style={{ fontSize: textPx(14), fontWeight: 500, color: colors.ink2 }}>
              {translate("analyticsPage.client.inactive_rule_suffix")}
            </span>
          </Flex>
        </Card>
        <Card styles={{ body: { padding: 18 } }}>
          <div style={{ marginBottom: 4 }}>
            <span style={{ fontSize: textPx(13), color: colors.ink, fontWeight: 800 }}>
              {data?.total_inactive.value ?? 0}
            </span>{" "}
            <span style={{ fontSize: textPx(13), color: colors.ink, fontWeight: 800 }}>
              {translate("analyticsPage.client.report_inactive")}
            </span>
          </div>
          <ReportTable<ClientInactiveRow>
            rowKey="customer_id"
            loading={isLoading}
            columns={columns}
            dataSource={data?.table ?? []}
            pageMemory="analyticsClientInactive"
            onSortChange={onSortChange}
            onRow={(row) => ({
              onClick: () => navigate(`${PATHS.CUSTOMER}/${row.customer_id}?tab=1`),
              style: { cursor: "pointer" },
            })}
          />
        </Card>
      </Flex>
    </>
  );
};

export default ClientInactivePage;
